/**
 * TalentOS — the score shown on a candidate row or card.
 *
 * WHY THIS EXISTS
 * ---------------
 * A candidate list and the report opened from it must show the same number.
 * AIRecruit's own list score comes from `job_matches.overall_score`, produced
 * by a different scorer with different dimensions and different weights — so
 * leaving the list on that number while the report shows TalentOS's makes the
 * same candidate read 72% on the card and 40% when opened. That contradiction
 * is worse than either figure alone, because it makes both look untrustworthy.
 *
 * Pass this the summary from `useTalentosMatchesForJob`. When TalentOS has no
 * run for a candidate the component renders `fallback` instead of inventing a
 * number, so a host list can keep its existing score during rollout and switch
 * over candidate-by-candidate as they get re-scored.
 */
import React from 'react'
import type { TalentosMatchSummary } from './talentosApi'
import {
  percentText,
  recommendationLabel,
  recommendationPillClasses,
  verdictDecidedByGate,
} from './talentosLabels'

interface TalentosScoreCellProps {
  /** From `useTalentosMatchesForJob(jobId).byResumeId.get(resumeId)`.
   *  `undefined` means this candidate has no TalentOS run. */
  summary: TalentosMatchSummary | undefined
  /** What to show when there is no run — typically the host's existing score. */
  fallback?: React.ReactNode
  /** Show the verdict pill under the number. Off in dense lists. */
  showRecommendation?: boolean
  /** Type classes for the number, so the score inherits the size and weight
   *  of whatever it replaces in the host list instead of imposing its own. */
  className?: string
  /** Called when the cell is clicked/activated — typically opens the
   *  candidate's detailed score report. Omitted: the cell renders as plain,
   *  non-interactive text, same as before this prop existed. */
  onClick?: () => void
}

export default function TalentosScoreCell({
  summary,
  fallback = null,
  showRecommendation = false,
  className = 'text-lg font-semibold',
  onClick,
}: TalentosScoreCellProps) {
  if (!summary) return <>{fallback}</>

  // An ineligible candidate was never scored. Rendering 0% here would say
  // "we measured this person and they scored zero", which is a different and
  // much harsher claim than "they did not meet the experience bar".
  const notScored = summary.display_percent === null

  // A gate failure forces 'not_recommended' whatever the score is. The number
  // stays accurate but stops being the verdict, so it must stop reading as
  // one: 78% in confident blue on a row for a rejected candidate is the same
  // contradiction this component's docstring exists to prevent, and it is
  // worse in a list than in a report because a list is scanned, not read.
  const gateDecided = verdictDecidedByGate(summary)
  // Which check, named. Greying alone says "something is off"; the caption
  // says what, which is the difference between a recruiter re-reading the row
  // and a recruiter knowing whether to open the report.
  const gateCaption =
    summary.mandatory_gate_status === 'failed'
      ? 'Essential requirements'
      : summary.skills_threshold_status === 'failed'
        ? 'Skill coverage'
        : null
  const muted = notScored || gateDecided

  return (
    <div
      className={`flex flex-col items-center gap-1 ${onClick ? 'cursor-pointer group' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      <div
        className={`font-mono tabular-nums leading-none ${className} ${
          muted ? 'text-gray-400' : 'text-blue-700'
        } ${onClick ? 'group-hover:underline' : ''}`}
        title={
          notScored
            ? `Not eligible — below the experience minimum${onClick ? '. Click to view details.' : ''}`
            : gateCaption
              ? `${gateCaption} not met — this decides the verdict, not the score${onClick ? '. Click to view details.' : ''}`
              : onClick
                ? 'Click to view detailed scores'
                : 'TalentOS match score'
        }
      >
        {notScored ? (
          <span className="text-sm font-semibold whitespace-nowrap">Not Eligible</span>
        ) : (
          <>
            {percentText(summary.display_percent)}
            <span className="text-xs font-medium">%</span>
          </>
        )}
      </div>
      {showRecommendation && (
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide whitespace-nowrap ${recommendationPillClasses(
            summary.recommendation,
            summary.gate_status,
          )}`}
        >
          {recommendationLabel(summary.recommendation, summary.gate_status)}
        </span>
      )}
    </div>
  )
}
